import React from "react";
import { Text } from "@chakra-ui/react";
import { FormikErrors, FormikProps, withFormik } from "formik";
import { Button } from "@chakra-ui/button";
import { Box, HStack } from "@chakra-ui/layout";
import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { TextField } from "../components/fields/TextField/TextField";
import { isEmptyStr } from "../util/stringHelper";
import { FormStack } from "../components/FormStack/FormStack";
import * as Yup from "yup";
import { ApiResult } from "../api/dataApiClient";
import { PasswordField } from "../components/fields/PasswordField/PasswordField";

const emptyStrToNull = (value: string | null): string | null => {
  if (!value || value.trim() === "") {
    return null;
  } else {
    return value;
  }
};

interface NewUserFormValues {
  username?: string;
  password?: string | null;
  reTypePassword?: string | null;
  displayName?: string;
}

interface NewUserFormProps {
  apiClient: AdminApiClient;
  onCancel: () => void;
  onSuccess: (integration: CherryHubAdminApi.UserAccount) => void;
  onFailure: (message?: string) => void;
}

const InnerForm = ({
  isSubmitting,
  submitForm,
  isValid,
  onCancel,
  values,
}: NewUserFormProps & FormikProps<NewUserFormValues>) => {
  return (
    <>
      <Box width="100%" maxW="800px" alignSelf="flex-start">
        <FormStack>
          <TextField label="Email" name="username" />
          <PasswordField label="Password" name="password" />
          {values.password && (
            <PasswordField label="Re-type Password" name="reTypePassword" />
          )}
          <TextField label="Display Name" name="displayName" />

          <HStack width="100%" justifyContent="start" spacing="3" pt="8">
            <Button
              colorScheme="cherryButton"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !isValid}
              onClick={submitForm}
            >
              Save
            </Button>
            <Button disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
          </HStack>
        </FormStack>
      </Box>
    </>
  );
};

export const NewUserForm = withFormik<NewUserFormProps, NewUserFormValues>({
  mapPropsToValues: (user) => {
    return {};
  },

  handleSubmit: async (values, { props, setFieldError }) => {
    let isValid = true;
    if (isEmptyStr(values.displayName)) {
      setFieldError("displayName", "Display name is required.");
      isValid = false;
    }

    // Translate form state to UserUpdate
    const userAccount: Partial<CherryHubAdminApi.NewUserAccount> = {
      DisplayName: values.displayName,
      Password: values.password,
      Username: values.username,
    };

    let result = await props.apiClient.createUser(userAccount);

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess(result.data);
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,

  validationSchema: Yup.object({
    displayName: Yup.string().required("Display Name is required!"),
    username: Yup.string().email().required("Username is required!"),
    reTypePassword: Yup.string()
      .optional()
      .test(function (value) {
        const { password } = this.parent;
        if (password)
          return value == password
            ? true
            : this.createError({
                message: "Password and Re-type Password are not matched!",
                path: "reTypePassword",
              });
        return true;
      }),
  }),
})(InnerForm);
